import {
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react'
import {
  FieldError,
  FieldValues,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form'
import { SwashbuckleComponentProps } from '../../../types'
import React from 'react'

interface InputProps<TFormValues extends FieldValues>
  extends SwashbuckleComponentProps,
    ChakraInputProps {
  error?: FieldError
  helperText?: string
  label: string
  leftElement?: string
  name: string
  register?: (name: string, RegisterOptions) => UseFormReturn<TFormValues>
  rightElement?: string
  rules?: RegisterOptions
  row?: boolean
  variant?: 'outline'
}

export const Input = <TFormValues extends FieldValues>({
  'data-qa': dataQa,
  className,
  error,
  helperText,
  label,
  leftElement,
  marginBottom, // This should be any Box prop rather than just margin. Check out BoxProps
  name,
  register,
  rightElement,
  rules,
  size = 'md',
  type = 'text',
  row = false,
  ...chakraInputProps
}: InputProps<TFormValues>) => {
  const isError = !!error

  return (
    <FormControl
      data-qa={dataQa}
      id={name}
      isInvalid={isError}
      marginBottom={marginBottom}
      className={className}
    >
      <FormLabel>{label}</FormLabel>
      <InputGroup size={size}>
        {leftElement && (
          <InputLeftElement pointerEvents='none' children={leftElement} />
        )}
        <ChakraInput
          type={type}
          {...register?.(name, rules)}
          {...chakraInputProps}
        />
        {rightElement && (
          <InputRightElement pointerEvents='none' children={rightElement} />
        )}
      </InputGroup>
      {isError ? (
        <FormErrorMessage>{error?.message}</FormErrorMessage>
      ) : (
        // If no helper text, we still want to display this element
        // so that the space for the error message is accounted for and we avoid
        // the inputs shifting in a form when errors appear
        <FormHelperText>{helperText || '\u200B'}</FormHelperText>
      )}
    </FormControl>
  )
}

Input.displayName = 'Input'
